/**
 * Everything the journal knows, in one block.
 *
 * ## Why this exists beside a configurable report
 *
 * The report above answers a question you already have. This answers the one
 * you did not know to ask. You cannot pick "max consecutive losing days" out of
 * a dropdown if you did not know the dropdown contained it, and `docs/23` §2.6
 * records the competitor replacing exactly this with four configurable cards
 * and making it worse for that case.
 *
 * ## Deliberately not configurable
 *
 * No metric picker, no chart, no pagination. Two columns of hairline rules. Its
 * whole value is that every figure is on screen at once — the moment it gains a
 * control, it becomes a worse version of the report above it.
 *
 * ## Nulls stay nulls
 *
 * `avgHoldSecondsLosers` is null when there are no losers, and this renders a
 * dash. Printing `0s` would assert an average over an empty set, and a trader
 * reading "average losing hold time: 0s" would reasonably conclude something
 * was broken — or worse, that they had no losers.
 */
import type { JSX } from 'react';
import { fmtNum, fmtPct, fmtUsd } from '../lib/api.js';
import { Pnl } from './panels.js';
import type { StatisticsResponse } from '@aegis/api-client';

/** `2h 14m`, `18m`, `47s`. Null renders as a dash by the caller. */
function fmtHold(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

type Row = readonly [label: string, value: string | JSX.Element];

const DASH = <span className="dim">—</span>;

const hold = (s: number | null): string | JSX.Element => (s === null ? DASH : fmtHold(s));

/**
 * `Pnl` rather than a local class, so a figure here is toned and glyphed
 * exactly as the same figure is on every other screen — and so meaning is
 * never carried by colour alone.
 */
const money = (v: string): JSX.Element => <Pnl value={v} />;

/** A cost, not a result. Always negative in effect and never toned green. */
const cost = (v: string): JSX.Element => <span className="dim">{fmtUsd(v)}</span>;

export function AllStatistics({ data }: { data: StatisticsResponse }): JSX.Element {
  const s = data.summary;
  const d = data.deep;

  const left: readonly Row[] = [
    ['Net P&L', money(s.netPnl)],
    ['Gross P&L', money(s.grossPnl)],
    ['Commissions', cost(s.commissions)],
    ['Fees', cost(s.fees)],
    ['Trades', String(s.tradeCount)],
    ['Winners', String(s.winCount)],
    ['Losers', String(s.lossCount)],
    ['Breakeven', String(s.scratchCount)],
    ['Open right now', String(d.openTradeCount)],
    ['Win rate', fmtPct(s.winRate)],
    ['Expectancy', money(s.expectancy)],
    ['Profit factor', s.profitFactor ? fmtNum(s.profitFactor, 2) : DASH],
    ['Average win', money(s.avgWin)],
    ['Average loss', money(s.avgLoss)],
    ['Largest win', money(s.largestWin)],
    ['Largest loss', money(s.largestLoss)],
    // Null when no trade recorded what was risked. `reconstruct.ts` refuses to
    // guess a risk basis, and this refuses to average over guesses.
    ['Average R', s.avgRMultiple ? fmtNum(s.avgRMultiple, 2) : DASH],
    ['Max win streak', String(s.maxWinStreak)],
    ['Max loss streak', String(s.maxLossStreak)],
    ['Total volume', fmtNum(d.totalVolume, 0)],
  ];

  const right: readonly Row[] = [
    ['Trading days', String(d.tradingDays)],
    ['Winning days', String(d.winningDays)],
    ['Losing days', String(d.losingDays)],
    ['Breakeven days', String(d.breakevenDays)],
    ['Max winning-day streak', String(d.maxWinningDayStreak)],
    ['Max losing-day streak', String(d.maxLosingDayStreak)],
    ['Average day', money(d.avgDailyPnl)],
    ['Average winning day', money(d.avgWinningDayPnl)],
    ['Average losing day', money(d.avgLosingDayPnl)],
    ['Best day', money(d.largestWinningDay)],
    ['Worst day', money(d.largestLosingDay)],
    ['Average daily volume', fmtNum(d.avgDailyVolume, 2)],
    ['Months traded', String(d.monthsTraded)],
    ['Best month', d.bestMonth
      ? <><span className="mono dim">{d.bestMonth.month}</span> {money(d.bestMonth.netPnl)}</>
      : DASH],
    ['Worst month', d.worstMonth
      ? <><span className="mono dim">{d.worstMonth.month}</span> {money(d.worstMonth.netPnl)}</>
      : DASH],
    ['Average month', money(d.avgMonthlyPnl)],
    ['Average hold', hold(d.avgHoldSeconds)],
    ['Average hold, winners', hold(d.avgHoldSecondsWinners)],
    ['Average hold, losers', hold(d.avgHoldSecondsLosers)],
    ['Average hold, breakeven', hold(d.avgHoldSecondsScratches)],
  ];

  return (
    <section className="allstats">
      <h3>Everything, under the same filter</h3>
      <p className="dim allstats-note">
        {data.matched === data.total
          ? `All ${data.total.toLocaleString('en-US')} trades.`
          : `${data.matched.toLocaleString('en-US')} of `
            + `${data.total.toLocaleString('en-US')} trades match the filter above.`}
        {' '}Win, loss and breakeven use your breakeven band, so these agree with
        every other screen.
      </p>

      <div className="allstats-cols">
        {[left, right].map((col, i) => (
          <dl className="allstats-col" key={i}>
            {col.map(([label, value]) => (
              <div className="allstats-row" key={label}>
                <dt>{label}</dt>
                <dd className="mono">{value}</dd>
              </div>
            ))}
          </dl>
        ))}
      </div>
    </section>
  );
}
